import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeleteModal,
  Icon,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
} from '@thinknet-gmbh/platform-common-ui';
import { PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';

import { useDeleteEmailTemplateById } from 'src/hooks/email-templates/delete-email-template-by-id.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';

import {
  MainWrapper,
  ClosedElementContainer,
  IconsWrapper,
  ExpandContainer,
} from './email-template-card.styles';

const EmailTemplateCard = ({
  openEdit,
  template,
  onChangePagination,
}: {
  openEdit: (id: string) => void;
  template: any;
  onChangePagination: () => void;
}) => {
  const { t } = useTranslation('settings');
  const [isOpenedDeleteModal, setIsOpenedDeleteModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { canDelete, canEdit } = useUserCanManageDocument({
    createdBy: template.createdBy,
    platformPermission: PLATFORM_PERMISSIONS.emailTemplates,
  });

  const deleteEmailTemplateById = useDeleteEmailTemplateById({
    onSuccess: () => setIsOpenedDeleteModal(false),
  });

  const onSubmitDelete = () => {
    deleteEmailTemplateById({ variables: { emailTemplateId: template._id } });
    onChangePagination();
  };

  return (
    <MainWrapper>
      <DeleteModal
        isOpened={isOpenedDeleteModal}
        onClose={() => setIsOpenedDeleteModal(false)}
        title={t('emailTemplate.deleteModal.title')}
        subTitle={t('emailTemplate.deleteModal.subtitle', { templateName: template.subject })}
        translate={t}
        onSubmit={onSubmitDelete}
      />
      <ExpandContainer onClick={() => setIsExpanded(!isExpanded)}>
        <Typography weight="bold">{template.subject}</Typography>
        <Icon
          icon={isExpanded ? ICONS.chevronUp : ICONS.chevronDown}
          color={
            isExpanded
              ? PLATFORM_THEME_COLORS.primary.black
              : PLATFORM_THEME_COLORS.grayVariations.dark
          }
          size={22}
          rightMargin={false}
        />
      </ExpandContainer>
      <ClosedElementContainer isExpanded={isExpanded}>
        <div dangerouslySetInnerHTML={{ __html: template.body }} />
        <IconsWrapper>
          {canEdit && (
            <Icon
              icon={ICONS.editAlt}
              color={PLATFORM_THEME_COLORS.primary.blue}
              size={22}
              rightMargin={false}
              onClick={() => openEdit(template._id)}
            />
          )}

          {canDelete && (
            <Icon
              icon={ICONS.trash}
              color={PLATFORM_THEME_COLORS.tone.red}
              size={22}
              rightMargin={false}
              onClick={() => setIsOpenedDeleteModal(true)}
            />
          )}
        </IconsWrapper>
      </ClosedElementContainer>
    </MainWrapper>
  );
};

export default EmailTemplateCard;
